import { stripGeneratedHeaders } from "./reverseTranspiler";

/**
 * Practido Python & JavaScript to Multi-Target Transpiler Engine
 * Transpiles Python / JS code to JavaScript, Python, Arduino C++, and Standard C++17 cleanly.
 */

export const convertPythonToJsCode = (pythonCode) => {
  const cleanCode = stripGeneratedHeaders(pythonCode);
  if (!cleanCode || !cleanCode.trim()) {
    return `console.log("Welcome to Practido Code Studio!");\n`;
  }

  const lines = cleanCode.split("\n");
  const resultLines = [];
  const indentStack = [0];

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    if (!trimmed) {
      resultLines.push("");
      continue;
    }

    const leadingSpaces = rawLine.match(/^\s*/)[0].length;

    while (indentStack.length > 1 && leadingSpaces < indentStack[indentStack.length - 1]) {
      indentStack.pop();
      const closeIndent = " ".repeat(indentStack[indentStack.length - 1]);
      resultLines.push(`${closeIndent}}`);
    }

    const currentIndentStr = " ".repeat(leadingSpaces);

    if (trimmed.startsWith("#")) {
      resultLines.push(`${currentIndentStr}// ${trimmed.slice(1).trim()}`);
      continue;
    }

    let line = trimmed;

    // Transpile Python f-strings: f"Hello {x}!" -> `Hello ${x}!`
    line = line.replace(/f(["'])(.*?)\1/g, (match, quote, content) => {
      const convertedContent = content.replace(/\{(.*?)\}/g, "${$1}");
      return `\`${convertedContent}\``;
    });

    // Transpile print(...) to console.log(...)
    if (line.startsWith("print(") && line.endsWith(")")) {
      const inner = line.slice(6, -1);
      line = `console.log(${inner});`;
    }

    // Function definition: def func(a, b):
    if (/^def\s+([a-zA-Z_]\w*)\s*\((.*?)\)\s*:$/.test(line)) {
      line = line.replace(/^def\s+([a-zA-Z_]\w*)\s*\((.*?)\)\s*:$/, "function $1($2) {");
      indentStack.push(leadingSpaces + 4);
    }
    // For loops: for i in range(1, 6): or for i in range(5):
    else if (/^for\s+([a-zA-Z_]\w*)\s+in\s+range\((.*?)\)\s*:$/.test(line)) {
      const match = line.match(/^for\s+([a-zA-Z_]\w*)\s+in\s+range\((.*?)\)\s*:$/);
      const varName = match[1];
      const rangeArgs = match[2].split(",").map((s) => s.trim());
      let start = "0";
      let end = "0";

      if (rangeArgs.length === 1) {
        end = rangeArgs[0];
      } else if (rangeArgs.length >= 2) {
        start = rangeArgs[0];
        end = rangeArgs[1];
      }

      line = `for (let ${varName} = ${start}; ${varName} < ${end}; ${varName}++) {`;
      indentStack.push(leadingSpaces + 4);
    }
    // For loop over iterable: for item in items:
    else if (/^for\s+([a-zA-Z_]\w*)\s+in\s+(.*?)\s*:$/.test(line)) {
      line = line.replace(/^for\s+([a-zA-Z_]\w*)\s+in\s+(.*?)\s*:$/, "for (const $1 of $2) {");
      indentStack.push(leadingSpaces + 4);
    }
    // While loop: while cond:
    else if (/^while\s+(.*?)\s*:$/.test(line)) {
      line = line.replace(/^while\s+(.*?)\s*:$/, "while ($1) {");
      indentStack.push(leadingSpaces + 4);
    }
    // If / Elif / Else
    else if (/^if\s+(.*?)\s*:$/.test(line)) {
      line = line.replace(/^if\s+(.*?)\s*:$/, "if ($1) {");
      indentStack.push(leadingSpaces + 4);
    } else if (/^elif\s+(.*?)\s*:$/.test(line)) {
      line = line.replace(/^elif\s+(.*?)\s*:$/, "} else if ($1) {");
      indentStack.push(leadingSpaces + 4);
    } else if (/^else\s*:$/.test(line)) {
      line = "} else {";
      indentStack.push(leadingSpaces + 4);
    } else {
      line = line
        .replace(/\bTrue\b/g, "true")
        .replace(/\bFalse\b/g, "false")
        .replace(/\bNone\b/g, "null");

      if (!line.endsWith("{") && !line.endsWith("}") && !line.endsWith(";")) {
        line += ";";
      }
    }

    resultLines.push(`${currentIndentStr}${line}`);
  }

  while (indentStack.length > 1) {
    indentStack.pop();
    const closeIndent = " ".repeat(indentStack[indentStack.length - 1]);
    resultLines.push(`${closeIndent}}`);
  }

  return resultLines.join("\n");
};

export const convertPythonToTarget = (pythonCode, targetLang) => {
  const cleanPython = stripGeneratedHeaders(pythonCode);
  if (!cleanPython || !cleanPython.trim()) {
    return `// Empty Workspace\n`;
  }

  const jsBody = convertPythonToJsCode(cleanPython);

  if (targetLang === "javascript") {
    return jsBody;
  }

  if (targetLang === "arduino") {
    const formattedBody = jsBody
      .split("\n")
      .map((l) => (l ? `  ${l}` : ""))
      .join("\n")
      .replace(/console\.log\(`(.*?)`\);/g, (m, p1) => {
        const clean = p1.replace(/\$\{(.*?)\}/g, '" + String($1) + "');
        return `Serial.println(String("${clean}"));`;
      })
      .replace(/console\.log\((.*?)\);/g, "Serial.println($1);")
      .replace(/let |const /g, "auto ");

    return `#include <Arduino.h>

void setup() {
  Serial.begin(9600);

${formattedBody}
}

void loop() {
  // Main repeating loop
  delay(100);
}
`;
  }

  if (targetLang === "cpp") {
    const formattedBody = jsBody
      .split("\n")
      .map((l) => (l ? `    ${l}` : ""))
      .join("\n")
      .replace(/console\.log\(`(.*?)`\);/g, (m, p1) => {
        const clean = p1.replace(/\$\{(.*?)\}/g, '" << $1 << "');
        return `std::cout << "${clean}" << std::endl;`;
      })
      .replace(/console\.log\((.*?)\);/g, "std::cout << $1 << std::endl;")
      .replace(/let |const /g, "auto ");

    return `#include <iostream>
#include <string>
#include <vector>

int main() {
${formattedBody}

    return 0;
}
`;
  }

  return cleanPython;
};

export const convertJsToTarget = (jsCode, targetLang) => {
  const cleanJs = stripGeneratedHeaders(jsCode);
  if (!cleanJs || !cleanJs.trim()) {
    return `// Empty Workspace\n`;
  }

  const codeStr = cleanJs.trim();

  if (targetLang === "python") {
    return codeStr
      .replace(/console\.log\((.*?)\);?/g, "print($1)")
      .replace(/function (.*?)\((.*?)\) \{/g, "def $1($2):")
      .replace(/for \(let (.*?) = (.*?); \1 < (.*?); \1\+\+\) \{/g, "for $1 in range($2, $3):")
      .replace(/else if \((.*?)\) \{/g, "elif $1:")
      .replace(/else \{/g, "else:")
      .replace(/\}/g, "")
      .replace(/\/\//g, "#");
  }

  if (targetLang === "arduino") {
    const formattedJs = codeStr
      .replace(/console\.log\(`(.*?)`\);?/g, (m, p1) => {
        const clean = p1.replace(/\$\{(.*?)\}/g, '" + String($1) + "');
        return `Serial.println(String("${clean}"));`;
      })
      .replace(/console\.log\((.*?)\);?/g, "Serial.println($1);")
      .replace(/let |const /g, "auto ");

    return `#include <Arduino.h>

void setup() {
  Serial.begin(9600);

  ${formattedJs.split("\n").join("\n  ")}
}

void loop() {
  delay(100);
}
`;
  }

  if (targetLang === "cpp") {
    const formattedJs = codeStr
      .replace(/console\.log\(`(.*?)`\);?/g, (m, p1) => {
        const clean = p1.replace(/\$\{(.*?)\}/g, '" << $1 << "');
        return `std::cout << "${clean}" << std::endl;`;
      })
      .replace(/console\.log\((.*?)\);?/g, "std::cout << $1 << std::endl;")
      .replace(/let |const /g, "auto ");

    return `#include <iostream>
#include <string>
#include <vector>

int main() {
    ${formattedJs.split("\n").join("\n    ")}

    return 0;
}
`;
  }

  return cleanJs;
};
